import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { META, pickPractice, recordAnswer } from "../lib/store";
import { RotateCw, Check, X, ChevronRight, Layers, RefreshCw } from "lucide-react";

const SIZES = [10, 20, 30];

export default function Flashcards() {
  const { level } = useApp();
  const cfg = META[level];
  const [config, setConfig] = useState(null);
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(false);
  const [known, setKnown] = useState(0);

  const start = (section, size) => {
    const c = pickPractice(level, size, section === "all" ? null : section);
    setCards(c);
    setConfig({ section, size });
    setIdx(0);
    setFlipped(false);
    setDone(false);
    setKnown(0);
  };

  const rate = (isKnown) => {
    const card = cards[idx];
    recordAnswer(level, card.id, isKnown);
    if (isKnown) setKnown((k) => k + 1);
    if (idx + 1 >= cards.length) {
      setDone(true);
      toast.success(`Session terminée · ${known + (isKnown ? 1 : 0)}/${cards.length} maîtrisées`);
    } else {
      setIdx((i) => i + 1);
      setFlipped(false);
    }
  };

  if (!config) {
    return (
      <Setup
        cfg={cfg}
        title="Cartes mémoires"
        subtitle="Retournez la carte pour voir la réponse, puis évaluez vos connaissances."
        onStart={start}
        icon={Layers}
      />
    );
  }

  if (done) {
    return (
      <Result known={known} total={cards.length} onRestart={() => setConfig(null)} label="Cartes révisées" />
    );
  }

  const card = cards[idx];
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => setConfig(null)} data-testid="fc-quit" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          Quitter
        </button>
        <span className="font-mono text-sm text-muted-foreground">
          {idx + 1} / {cards.length}
        </span>
      </div>
      <div className="h-1.5 rounded-sm bg-muted overflow-hidden">
        <div className="h-full bg-primary transition-[width]" style={{ width: `${(idx / cards.length) * 100}%` }} />
      </div>

      <div className="flip-card h-[340px] sm:h-[380px] cursor-pointer" onClick={() => setFlipped((f) => !f)} data-testid="flashcard">
        <AnimatePresence mode="wait">
          <motion.div
            key={card.id + (flipped ? "-b" : "-f")}
            initial={{ rotateY: flipped ? -90 : 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="flip-inner w-full h-full"
          >
            <div className="w-full h-full rounded-sm border border-border bg-card p-6 sm:p-10 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs text-primary">{card.id}</span>
                <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  {flipped ? "Réponse" : "Question"}
                </span>
              </div>
              <div className="flex-1 grid place-items-center text-center">
                {flipped ? (
                  <p className="font-display font-bold text-xl sm:text-2xl text-success leading-snug">
                    {card.options[card.answer]}
                  </p>
                ) : (
                  <p className="font-display font-medium text-lg sm:text-2xl leading-snug">{card.question}</p>
                )}
              </div>
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-4">
                <RotateCw size={14} /> Touchez pour retourner
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {flipped ? (
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => rate(false)} data-testid="fc-unknown" className="inline-flex items-center justify-center gap-2 rounded-sm border border-destructive/50 text-destructive px-4 py-3 font-semibold hover:bg-destructive/10 transition-colors">
            <X size={18} /> À revoir
          </button>
          <button onClick={() => rate(true)} data-testid="fc-known" className="inline-flex items-center justify-center gap-2 rounded-sm bg-success text-white px-4 py-3 font-semibold hover:-translate-y-px transition-transform">
            <Check size={18} /> Je savais
          </button>
        </div>
      ) : (
        <button onClick={() => setFlipped(true)} data-testid="fc-reveal" className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-3 font-semibold hover:-translate-y-px transition-transform">
          Voir la réponse <ChevronRight size={18} />
        </button>
      )}
    </div>
  );
}

export function Setup({ cfg, title, subtitle, onStart, icon: Icon, sizes = SIZES, ctaLabel = "Démarrer" }) {
  const [section, setSection] = useState("all");
  const [size, setSize] = useState(sizes[1]);
  return (
    <div className="max-w-2xl space-y-8">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-12 h-12 rounded-sm bg-primary/10 text-primary">
          <Icon size={24} />
        </span>
        <div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">{title}</h1>
          <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-6 space-y-6">
        <div>
          <div className="text-sm font-semibold mb-3">Section</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              onClick={() => setSection("all")}
              data-testid="setup-section-all"
              className={`text-left rounded-sm border px-3 py-2.5 text-sm transition-colors ${
                section === "all" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              Toutes les sections <span className="font-mono text-xs">({cfg.total})</span>
            </button>
            {cfg.sections.map((s) => (
              <button
                key={s.code}
                onClick={() => setSection(s.code)}
                data-testid={`setup-section-${s.code}`}
                className={`text-left rounded-sm border px-3 py-2.5 text-sm transition-colors ${
                  section === s.code ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <span className="font-mono text-xs text-primary mr-1">{s.code}</span> {s.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm font-semibold mb-3">Nombre de questions</div>
          <div className="flex gap-2">
            {sizes.map((n) => (
              <button
                key={n}
                onClick={() => setSize(n)}
                data-testid={`setup-size-${n}`}
                className={`flex-1 rounded-sm border px-4 py-3 font-mono font-semibold transition-colors ${
                  size === n ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => onStart(section, size)}
          data-testid="setup-start"
          className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-3.5 font-semibold hover:-translate-y-px transition-transform"
        >
          {ctaLabel} <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

export function Result({ known, total, onRestart, label }) {
  const pct = Math.round((known / total) * 100);
  return (
    <div className="max-w-lg mx-auto text-center space-y-6 py-8">
      <div className="font-mono text-6xl font-bold tabular" style={{ color: pct >= 70 ? "hsl(var(--success))" : "hsl(var(--warning))" }}>
        {pct}%
      </div>
      <h1 className="font-display font-black text-2xl">
        {known} / {total} {label}
      </h1>
      <button onClick={onRestart} data-testid="result-restart" className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-6 py-3 font-semibold hover:-translate-y-px transition-transform">
        <RefreshCw size={18} /> Nouvelle session
      </button>
    </div>
  );
}
